import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { employeesAPI, hrDocsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, FileText, Download, Eye } from 'lucide-react';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const PURPOSES = ['general', 'bank loan', 'visa application', 'rental agreement', 'embassy', 'custom'];

const SalaryCertificateGenerator = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ employee_id: '', purpose: 'general', custom_body: '' });
  const [preview, setPreview] = useState(null);

  const allowedRole = !user || ['super_admin', 'hr_admin'].includes(user?.role);

  const fetchAll = async () => {
    try {
      const [e, h] = await Promise.all([employeesAPI.getAll(), hrDocsAPI.list({ document_type: 'salary_certificate' })]);
      setEmployees(e.data); setHistory(h.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (allowedRole) fetchAll(); /* eslint-disable-next-line */ }, [allowedRole]);

  const generate = async (download) => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    setGenerating(true);
    try {
      const payload = { ...form, custom_body: form.purpose === 'custom' ? form.custom_body : null };
      const r = await hrDocsAPI.generateSalaryCertificate(payload);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const emp = employees.find(e => e.id === form.employee_id);
      const name = emp ? `${emp.first_name}_${emp.last_name}` : 'employee';
      if (download) downloadBlob(blob, `salary_certificate_${name}.pdf`);
      else setPreview(URL.createObjectURL(blob));
      toast.success('Generated successfully');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const downloadHistory = async (id, filename) => {
    try {
      const r = await hrDocsAPI.download(id);
      downloadBlob(new Blob([r.data], { type: 'application/pdf' }), filename);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowedRole) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="salary-cert-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileText />Salary Certificate Generator</h1>
        <p className="text-muted-foreground">Issue letterhead salary certificates for banks, visas, embassies and more</p>
      </div>

      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate" data-testid="tab-generate">Generate</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Employee *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                    <SelectTrigger data-testid="sc-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Purpose</Label>
                  <Select value={form.purpose} onValueChange={(v) => setForm({...form, purpose: v})}>
                    <SelectTrigger data-testid="sc-purpose"><SelectValue /></SelectTrigger>
                    <SelectContent>{PURPOSES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.purpose === 'custom' && (
                  <div>
                    <Label>Custom Certificate Body</Label>
                    <Textarea value={form.custom_body} onChange={(e) => setForm({...form, custom_body: e.target.value})} rows={6} placeholder="Enter the full body text. Letterhead, signatory and stamp are appended automatically." data-testid="sc-body" />
                  </div>
                )}
                <div className="bg-muted/40 rounded p-3 text-sm">
                  <p className="font-medium mb-1">Auto-filled from employee record:</p>
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                    <li>Full name, employee code, designation, department</li>
                    <li>Joining date</li>
                    <li>Annual gross salary (from salary structure)</li>
                    <li>Company name, country & branded letterhead</li>
                  </ul>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => generate(false)} disabled={generating} data-testid="sc-preview-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Eye size={14} className="mr-2" />}Preview
                  </Button>
                  <Button onClick={() => generate(true)} disabled={generating} data-testid="sc-download-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
              <CardContent>
                {preview ? (
                  <iframe src={preview} className="w-full" style={{ height: 600 }} title="preview" data-testid="sc-preview-iframe" />
                ) : (
                  <div className="border-2 border-dashed rounded p-12 text-center text-muted-foreground">
                    Click <strong>Preview</strong> to see the generated PDF here, or <strong>Download PDF</strong> to save it directly.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Purpose</TableHead>
                <TableHead>Generated</TableHead><TableHead>By</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No salary certificates generated yet</TableCell></TableRow>}
                {history.map(d => (
                  <TableRow key={d.id} data-testid={`history-row-${d.id}`}>
                    <TableCell className="font-medium">{d.employee_name || '—'}<div className="text-xs text-muted-foreground">{d.employee_code}</div></TableCell>
                    <TableCell>{d.template_data?.purpose || 'general'}</TableCell>
                    <TableCell className="text-xs">{new Date(d.generated_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{d.generated_by}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => downloadHistory(d.id, d.filename)} data-testid={`download-${d.id}`}>
                        <Download size={12} className="mr-1" />PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalaryCertificateGenerator;
